import { computed, ref, Ref, watch } from "vue";
import { isEqual } from "lodash";
import { useDocumentationQuery } from "./documentation-query";
import { useQuery } from "@vue/apollo-composable";
import { getQueryTopicIsAllowedInContext } from "@/abilities/documentation/graphql/toc.query.ts";
import { useTocLoader } from "@/abilities/documentation/composables/toc-loader.ts";
import { watchImmediate } from "@vueuse/core";

export interface TocNode {
    id: string;
    label: string;
    children?: Array<TocNode>;
    lazy: boolean;
    childPath: string;
    path: string;
}

export interface TocNavigation {
    title: string;
    id: string;
}

export type DocumentStructureContext = {
    contentMapId: Ref<string>;
    topicId: Ref<string | undefined>;
    productId?: Ref<string | undefined>;
    assetId?: Ref<string | undefined>;
    mechanicId?: Ref<string | undefined>;
    isSearchEnabled?: Ref<boolean>;
};

export const useDocumentStructure = ({
    contentMapId,
    topicId,
    productId,
    assetId,
    mechanicId,
    isSearchEnabled,
}: DocumentStructureContext) => {
    const toc = ref<TocNode[]>([]);
    const loading = ref(true);
    const breadCrumbsLoading = ref(true);
    const tocLoadingError = ref<Error>();
    const breadCrumbs = ref<{ id: string; label: string }[]>();

    const { loadToc } = useTocLoader(contentMapId);

    const {
        title,
        document,
        pathNotFound,
        childPath: documentChildPath,
        childPathForBreadCrumb,
        path: documentPath,
        error: documentError,
    } = useDocumentationQuery(
        contentMapId,
        computed(() => topicId.value)
    );

    const allowedInContextQuery = computed(() =>
        getQueryTopicIsAllowedInContext(productId?.value, assetId?.value, mechanicId?.value)
    );

    const { loading: loadingAllowedInContext, result: resultAllowedInContext } = useQuery(
        allowedInContextQuery,
        {
            filter: computed(() => {
                return {
                    ids: [`${topicId.value}@${contentMapId.value}`],
                };
            }),
            productId: productId?.value,
            assetId: `${productId?.value}_${assetId?.value}`,
            mechanicId: mechanicId?.value,
        },
        computed(() => {
            return {
                fetchPolicy: "cache-first",
                enabled: !!topicId.value && !!contentMapId.value && !!allowedInContextQuery.value,
            };
        })
    );

    const topicNotFound = computed(() => {
        const notAllowedInContext =
            allowedInContextQuery.value &&
            !loadingAllowedInContext.value &&
            !(
                resultAllowedInContext.value?.product?.relatedContents?.contents?.[0]?.node?.id ||
                resultAllowedInContext.value?.asset?.relatedContents?.contents?.[0]?.node?.id ||
                resultAllowedInContext.value?.mechanic?.relatedContents?.contents?.[0]?.node?.id
            );

        return pathNotFound.value || notAllowedInContext;
    });

    watchImmediate(topicNotFound, () => {
        if (topicNotFound.value) {
            loading.value = false;
        }
    });

    const calculateBreadCrumb = (flatTocCache: Record<string, TocNode>) => {
        breadCrumbsLoading.value = true;
        const parts = childPathForBreadCrumb.value?.split("/");

        if (!parts || loading.value) {
            breadCrumbsLoading.value = false;
            return undefined;
        }

        let absolutePath = "";
        const _breadCrumbs = [];
        for (const part of parts) {
            absolutePath += (absolutePath ? "/" : "") + part;
            if (part === "~") continue;
            const label = flatTocCache[absolutePath]?.label;
            const id = flatTocCache[absolutePath]?.id;
            _breadCrumbs.push({ id, label });
        }
        breadCrumbsLoading.value = false;
        return _breadCrumbs;
    };

    const load = async (loadUntil: string) => {
        const parts = loadUntil.split("/");
        let absolutePath = "";
        const flatTocCache: Record<string, TocNode> = {};

        let loadedToc: TocNode[] | undefined;

        try {
            for (const part of parts) {
                absolutePath += (absolutePath ? "/" : "") + part;
                const result = await loadToc(absolutePath, undefined, absolutePath === loadUntil, flatTocCache);

                loadedToc = isEqual(toc.value, result) ? undefined : result;
            }

            if (loadedToc) toc.value = loadedToc;

            loading.value = false;
            breadCrumbs.value = calculateBreadCrumb(flatTocCache);
        } catch (error: unknown) {
            console.error(error);
            tocLoadingError.value = error instanceof Error ? error : new Error(String(error));
            loading.value = false;
        }
    };

    const loadTocSubNode = async (childPath: string, done: (tocNode: TocNode[]) => void) => {
        await loadToc(childPath, done, false, {});
    };

    watch(
        [documentChildPath, documentPath],
        async () => {
            //if the TOC search is active, the paths do not need to be loaded -> in this case, the TOC is already fully loaded
            if ((!documentChildPath.value && !documentPath.value) || isSearchEnabled?.value) {
                return;
            }

            //first child node should be loaded
            if (documentChildPath.value) {
                await load(documentChildPath.value);
            } else if (documentPath.value) {
                await load(documentPath.value);
            }
        },
        { deep: true }
    );

    const error = computed(() => {
        const anyError = tocLoadingError.value || documentError.value;
        if (anyError) {
            loading.value = false;
        }
        return anyError;
    });

    const expanded = computed(() => {
        let expandedList: string[] = [];

        if (documentChildPath.value) expandedList = documentChildPath.value.substring(2).split("/");
        else if (documentPath.value) expandedList = documentPath.value.substring(2).split("/");

        return expandedList;
    });

    return {
        toc,
        title,
        error,
        loading,
        document,
        breadCrumbs,
        loadTocSubNode,
        breadCrumbsLoading,
        notFound: topicNotFound,
        expanded,
    };
};
